import { z } from "zod";
import { searchQuery } from "../http";
import { activityFilterQuerySchema } from "./activity-filter";
import { activityTimeSchema } from "./activity-time";
import { assigneeFilterSchema, assigneeSchema } from "./assignments";
import { directoryFilterQuerySchema } from "./record-filter";
import { reviewStateSchema } from "./review";
import { activitySortKeys, peopleSortKeys, type SortQuery, sortQuerySchema } from "./sorting";
import { tagFilterSchema, tagSchema } from "./tags";
export function activityVerb(type: string, kind: string, baseline: boolean) {
  if (type === "post") return baseline ? "was discovered through a post" : "posted";
  if (!["star", "issue", "comment", "fork", "watcher"].includes(type))
    return baseline ? "was discovered" : "had activity observed";
  if (baseline)
    return type === "star"
      ? kind === "observed"
        ? "was discovered through a public star event"
        : "was discovered in the stargazer roster"
      : `was discovered through ${type === "issue" ? "an issue" : type === "comment" ? "a comment" : type === "fork" ? "a fork" : "a watch relationship"}`;
  if (type === "star")
    return kind === "removed"
      ? "no longer appeared in the stargazer roster"
      : kind === "observed"
        ? "starred · public event observed"
        : kind === "restarred"
          ? "starred again"
          : "starred";
  // State transitions do not identify who made the change. The author is context,
  // not necessarily the person who closed, reopened, or edited an issue.
  if (type === "issue" && kind !== "added") return `authored an issue · ${kind} observed`;
  if (type === "comment")
    return kind === "added" ? "commented" : `authored a comment · ${kind} observed`;
  if (type === "issue") return "opened an issue";
  if (type === "fork")
    return kind === "removed"
      ? "had a fork disappear from the roster"
      : kind === "returned"
        ? "had a fork reappear in the roster"
        : "forked";
  return kind === "removed"
    ? "no longer appeared in the watcher roster"
    : "appeared in the watcher roster";
}
export function pageNumber(value?: string) {
  const page = Number(value);
  return Number.isSafeInteger(page) && page > 0 ? page : 1;
}
export function period(value?: string) {
  return value === "1" ? 1 : value === "30" ? 30 : 7;
}
// History views default to all time; only known ranges narrow the window.
export function rangeDays(value?: string) {
  return value === "1" ? 1 : value === "7" ? 7 : value === "30" ? 30 : 0;
}

// Plain-text previews only; source Markdown is never executed or injected as HTML.
export function conversationPreview(body: string) {
  return body
    .replace(/<!--[\s\S]*?(?:-->|$)/g, "")
    .replace(/```[^\n]*\n([\s\S]*?)```/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]*>/g, "")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/\*\*|`/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// One signal describes the source and its observed transition. Passive labels
// preserve the distinction between an issue's author and the actor who changed it.
export const signalLabels = {
  post_added: "Post added",
  post_baseline: "Post discovered",
  post_observed: "Post observed",
  star_added: "Star added",
  star_observed: "Starred",
  star_removed: "Star removed",
  star_restarred: "Star added again",
  star_baseline: "Stargazer discovered",
  issue_added: "Issue opened",
  issue_open: "Open issue",
  issue_closed: "Issue closed",
  issue_reopened: "Issue reopened",
  issue_updated: "Issue updated",
  issue_observed: "Issue observed",
  issue_baseline: "Issue discovered",
  comment_added: "Comment added",
  comment_updated: "Comment updated",
  comment_observed: "Comment observed",
  comment_baseline: "Comment discovered",
  fork_added: "Fork added",
  fork_removed: "Fork removed",
  fork_returned: "Fork returned",
  fork_observed: "Fork observed",
  fork_baseline: "Fork discovered",
  watcher_added: "Watcher added",
  watcher_removed: "Watcher removed",
  watcher_returned: "Watcher returned",
  watcher_observed: "Watcher observed",
  watcher_baseline: "Watcher discovered",
  observed: "Activity observed",
} as const;
export type ActivitySignal = keyof typeof signalLabels;
export function activitySignal(type: string, kind: string, baseline: boolean): ActivitySignal {
  const key = `${type}_${baseline ? "baseline" : kind}`;
  if (Object.hasOwn(signalLabels, key)) return key as ActivitySignal;
  const observed = `${type}_observed`;
  return Object.hasOwn(signalLabels, observed) ? (observed as ActivitySignal) : "observed";
}

export function activityTime(item: {
  kind: string;
  occurred_at: string | null;
  observed_at: string;
}) {
  // For state changes, occurred_at belongs to the original issue/fork, not the change.
  return ["added", "baseline", "restarred", "observed"].includes(item.kind)
    ? item.occurred_at || item.observed_at
    : item.observed_at;
}

export function personReviewKey(id: string) {
  return `person:${id}`;
}
export function sourceName(provider: string) {
  return provider === "github"
    ? "GitHub"
    : provider === "slack"
      ? "Slack"
      : provider.charAt(0).toUpperCase() + provider.slice(1);
}
export function signalLabel(activity: {
  type: string;
  kind: string;
  baseline: boolean;
  signal?: string | null;
}) {
  const known = activitySignal(activity.type, activity.kind, activity.baseline);
  return known === "observed" && activity.signal
    ? activity.signal
        .split(".")
        .slice(1)
        .join(" ")
        .replace(/_/g, " ")
        .replace(/^./, (s) => s.toUpperCase())
    : signalLabels[known];
}

export const commentContextSchema = z.object({
  kind: z.enum(["post", "issue", "pull_request"]),
  number: z.string().optional(),
  title: z.string().nullable(),
  url: z.string(),
});
export const activityItemSchema = z.object({
  id: z.string(),
  person_id: z.union([z.string(), z.null()]),
  assignedTo: assigneeSchema.nullable().optional(),
  provider: z.string(),
  context: z.string(),
  type: z.string(),
  kind: z.string(),
  url: z.string(),
  title: z.string(),
  body: z.string(),
  commentContext: commentContextSchema.nullable().optional(),
  occurred_at: z.union([z.string(), z.null()]),
  observed_at: z.string(),
  baseline: z.boolean(),
  login: z.union([z.string(), z.null()]),
  name: z.union([z.string(), z.null()]),
  avatar: z.union([z.string(), z.null()]),
  follow_up: z.union([z.string(), z.null()]),
  signal: z.union([z.string(), z.null()]).optional(),
  actor_known: z.boolean().optional(),
  /** The person's own review state; null when the signal has no person. */
  person_review: reviewStateSchema.nullable().optional(),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;
export type ActivityFilter = SortQuery<(typeof activitySortKeys)[number]> & {
  days?: number;
  repository?: string;
  type?: string;
  q?: string;
  topic?: string;
  person?: string;
  baseline?: boolean;
  page?: number;
  review?: string;
};

export const activityCursorSchema = z.union([
  z.object({
    order: z.literal("occurred"),
    at: z.string(),
    id: z.string(),
  }),
  z.object({
    order: z.literal("observed").optional(),
    at: z.string(),
    id: z.string(),
  }),
]);
export type ActivityCursor = z.infer<typeof activityCursorSchema>;
export const personSummarySchema = z.object({
  tags: z.array(tagSchema).optional(),
  id: z.string(),
  provider: z.string(),
  review_key: z.string(),
  login: z.string(),
  name: z.union([z.string(), z.null()]),
  avatar: z.string(),
  company: z.union([z.string(), z.null()]),
  company_id: z.string().nullable(),
  location: z.union([z.string(), z.null()]),
  latest_activity: z.union([
    z.object({
      title: z.string(),
      type: z.string(),
      kind: z.string(),
      baseline: z.boolean(),
      at: z.string(),
      source: z.string(),
    }),
    z.null(),
  ]),
  sources: z.array(z.string()),
  source_contexts: z.array(z.object({ provider: z.string(), name: z.string() })),
  first_seen_at: z.string(),
  contexts: z.number(),
});
export type PersonSummary = z.infer<typeof personSummarySchema>;
export const peopleDirectorySchema = z.object({
  items: z.array(personSummarySchema),
  total: z.number(),
  page: z.number(),
});
export type PeopleDirectory = z.infer<typeof peopleDirectorySchema>;
/** A day on the audience dashboard, in the same vocabulary the inbox uses. */
export interface AudienceTrendDay {
  day: string;
  actions: number;
  people: number;
  issues: number;
  comments: number;
  stars: number;
  discovered: number;
}
export interface AudienceIngestionDay {
  day: string;
  runs: number;
  fetched: number;
  created: number;
}
export interface AudienceSourceSummary {
  id: string;
  name: string;
  provider: string;
  resource: string;
  enabled: boolean;
  signals: number;
  people: number;
  /** Lifetime records the collector has fetched for this source. */
  total: number;
  last_sync_at: string | null;
  last_ingested_at: string | null;
}
export interface AudienceWorkflowSummary {
  key: string;
  runs: number;
  fetched: number;
  created: number;
  failed: number;
  running: number;
  last_run_at: string | null;
  last_status: string | null;
}
export interface AudienceRunSummary {
  workflow_id: string;
  run_id: string;
  workflow_key: string;
  source_name: string;
  status: string;
  trigger: string;
  started_at: string;
  closed_at: string | null;
  fetched: number;
  created: number;
  error: string | null;
}
export interface AudienceOverview {
  days: number;
  metrics: {
    active_people: number;
    new_people: number;
    companies: number;
    actions: number;
    issues: number;
    comments: number;
    discovered: number;
    runs: number;
    fetched: number;
    created: number;
  };
  trend: AudienceTrendDay[];
  kinds: Array<{ kind: string; count: number; people: number }>;
  providers: Array<{ provider: string; count: number; people: number }>;
  ingestion: AudienceIngestionDay[];
  sources: AudienceSourceSummary[];
  workflows: AudienceWorkflowSummary[];
  runs: AudienceRunSummary[];
  people: Array<{
    id: string;
    name: string;
    company: string | null;
    company_id: string | null;
    signals: number;
    last_seen_at: string;
  }>;
  companies: Array<{ id: string; name: string; people: number; signals: number }>;
}

export const peopleQuerySchema = searchQuery.extend({
  filter: directoryFilterQuerySchema("person").optional(),
  assignee: assigneeFilterSchema,
  tags: tagFilterSchema,
  ...sortQuerySchema(peopleSortKeys).shape,
  state: z.string().optional(),
  companyId: z.uuid().optional(),
  repository: z.string().optional(),
});
export const activityQuerySchema = searchQuery.extend({
  assignee: assigneeFilterSchema,
  filter: activityFilterQuerySchema.optional(),
  time: activityTimeSchema.optional(),
  person: z.string().optional(),
  repository: z.string().optional(),
  type: z.string().optional(),
  review: z.string().optional(),
  topic: z.string().optional(),
  ...sortQuerySchema(activitySortKeys).shape,
});
